import { Drawer } from 'antd';
import { Fragment } from 'react';
import type {
  WorkflowRunNodeAttempt,
  WorkflowRunNodeDetail,
} from '@/apis/logiblocksAPI';
import { CloseDrawerIcon } from '@/assets/icons/icons';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import type { ConditionalData } from '@/proto/logiblocks/logiblocks';
import { formatDateTime } from '../dateTimeUtils';
import type { NodeType } from '../nodes/registry';
import { ConditionalAttemptDetail } from './ConditionalAttemptDetail';
import {
  getDateTimeSetConditions,
  type DateTimeSetCondition,
} from './conditionalReportUtils';
import { DelayWaitCard, parseDelayWaitDetail } from './DelayWaitCard';
import { RunCompletedIcon, RunFailedIcon } from './runStatusIcons';
import styles from './RunNodeAttemptsDrawer.module.css';

const DRAWER_WIDTH = 420;

/** A request/response detail dict's conventional keys, read defensively --
 * the shape is node-type-specific and not guaranteed by the type system. */
function stringField(
  detail: Record<string, unknown>,
  key: string
): string | null {
  const value = detail[key];
  return typeof value === 'string' ? value : null;
}

function numberField(
  detail: Record<string, unknown>,
  key: string
): number | null {
  const value = detail[key];
  return typeof value === 'number' ? value : null;
}

function booleanField(detail: Record<string, unknown>, key: string): boolean {
  return detail[key] === true;
}

function AttemptCard({ attempt }: { attempt: WorkflowRunNodeAttempt }) {
  const { dateFormat } = useUserDateTimeFormat();
  const method = stringField(attempt.request_detail, 'method');
  const url = stringField(attempt.request_detail, 'url');
  const statusCode = numberField(attempt.response_detail, 'status_code');
  const body = stringField(attempt.response_detail, 'body');
  // Attempts recorded before the request body was stored have no `body` key,
  // which is different from a request that sent an empty one.
  const requestBody = stringField(attempt.request_detail, 'body');
  const requestContentType = stringField(
    attempt.request_detail,
    'content_type'
  );
  const requestBodyTruncated = booleanField(
    attempt.request_detail,
    'body_truncated'
  );

  return (
    <div className={styles.attemptCard}>
      <div className={styles.attemptHeader}>
        <span className={styles.attemptTitle}>
          {attempt.success ? (
            <RunCompletedIcon className={styles.statusIcon} size={14} />
          ) : (
            <RunFailedIcon className={styles.statusIcon} size={14} />
          )}
          <span
            className={
              attempt.success ? styles.attemptSucceeded : styles.attemptFailed
            }
          >
            Attempt {attempt.attempt_number}
          </span>
        </span>
        <span className={styles.attemptTime}>
          {formatDateTime(attempt.attempted_at, dateFormat)}
        </span>
      </div>

      {(method || url) && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Request</div>
          <div className={styles.fieldValue}>
            {method && <span className={styles.methodBadge}>{method}</span>}
            {url && <span className={styles.urlValue}>{url}</span>}
          </div>
        </div>
      )}

      {requestBody !== null && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>
            Request body
            {requestContentType && (
              <span className={styles.truncatedTag}>
                {' '}
                ({requestContentType})
              </span>
            )}
            {requestBodyTruncated && (
              <span className={styles.truncatedTag}> (truncated)</span>
            )}
          </div>
          {requestBody ? (
            <pre className={styles.responseBody}>{requestBody}</pre>
          ) : (
            <div className={styles.fieldValue}>No body sent</div>
          )}
        </div>
      )}

      <div className={styles.fieldRow}>
        {statusCode !== null && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Status</div>
            <div className={styles.fieldValue}>{statusCode}</div>
          </div>
        )}
        {attempt.duration_ms !== null && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Duration</div>
            <div className={styles.fieldValue}>{attempt.duration_ms}ms</div>
          </div>
        )}
        {attempt.credential_name && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Credential</div>
            <div className={styles.fieldValue}>{attempt.credential_name}</div>
          </div>
        )}
      </div>

      {attempt.error_message && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Error</div>
          <div className={`${styles.fieldValue} ${styles.errorValue}`}>
            {attempt.error_message}
          </div>
        </div>
      )}

      {body && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>
            Response body
            {attempt.response_detail_truncated && (
              <span className={styles.truncatedTag}> (truncated)</span>
            )}
          </div>
          <pre className={styles.responseBody}>{body}</pre>
        </div>
      )}
    </div>
  );
}

/** Returns the date/time set conditions `row`'s Conditional report covers, in graph order. */
function getRowDateTimeSetConditions(
  row: WorkflowRunNodeDetail,
  conditional: ConditionalData | null
): DateTimeSetCondition[] {
  if (row.report?.conditional === undefined || conditional === null) {
    return [];
  }
  return getDateTimeSetConditions(row.report.conditional, conditional);
}

/** Shows how a Conditional node's date/time set conditions evaluated during one node run. */
function EvaluationCard({
  completedAt,
  conditions,
}: {
  completedAt: string | null;
  conditions: DateTimeSetCondition[];
}) {
  const { dateFormat } = useUserDateTimeFormat();
  return (
    <div className={styles.attemptCard}>
      <div className={styles.attemptHeader}>
        <span className={styles.attemptTitle}>
          <RunCompletedIcon className={styles.statusIcon} size={14} />
          <span className={styles.attemptSucceeded}>Evaluation</span>
        </span>
        {completedAt !== null && (
          <span className={styles.attemptTime}>
            {formatDateTime(completedAt, dateFormat)}
          </span>
        )}
      </div>
      <ConditionalAttemptDetail conditions={conditions} />
    </div>
  );
}

/**
 * Right-side drawer listing the attempts of a run-history node that retries, oldest first:
 * every outbound call a Call Webhook made (method, URL, status, latency, credential, truncated
 * request/response detail). A Conditional node's run shows an Evaluation card with its date/time
 * set conditions instead, and a Delay until event's attempt shows what it waits for and why it
 * stopped. Opened by clicking a node while a run is selected in the Runs tab; a node with none of
 * these shows an empty state.
 */
export function RunNodeAttemptsDrawer({
  nodeId,
  nodeType,
  rows,
  conditional,
  open,
  onClose,
}: {
  nodeId: string | null;
  nodeType: NodeType | null;
  /** The node's status rows in the run, in the order the run reached them. A row's status tells
   * a Delay that is waiting from one that was cut short. */
  rows: WorkflowRunNodeDetail[];
  /** The node's Conditional data from the graph that ran, if the node is a Conditional. */
  conditional: ConditionalData | null;
  open: boolean;
  onClose: () => void;
}) {
  const rowConditions = rows.map((row) =>
    getRowDateTimeSetConditions(row, conditional)
  );
  const hasCards = rows.some(
    (row, index) => row.attempts.length > 0 || rowConditions[index].length > 0
  );
  return (
    <Drawer
      title={
        <div className={styles.headerRow}>
          <span>Node attempts</span>
          <button
            type='button'
            onClick={onClose}
            className={styles.closeButton}
            aria-label='Close'
          >
            <CloseDrawerIcon />
          </button>
        </div>
      }
      placement='right'
      width={DRAWER_WIDTH}
      open={open}
      onClose={onClose}
      closable={false}
      mask={false}
      className={styles.drawer}
      styles={{
        body: { padding: 0 },
        header: { border: 'none', padding: '16px 16px 8px' },
      }}
      data-testid='run-node-attempts-drawer'
    >
      <div className={styles.content}>
        {!hasCards ? (
          <div className={styles.emptyState}>
            No attempts recorded for this node.
          </div>
        ) : (
          rows.map((row, index) => (
            <Fragment key={`${row.node_id}:${row.started_at}`}>
              {rowConditions[index].length > 0 && (
                <EvaluationCard
                  completedAt={row.completed_at}
                  conditions={rowConditions[index]}
                />
              )}
              {row.attempts.map((attempt) => {
                const key = `${nodeId ?? ''}:${attempt.attempt_number}:${attempt.attempted_at}`;
                const delayDetail =
                  nodeType === 'delay_action'
                    ? parseDelayWaitDetail(attempt.response_detail)
                    : null;
                if (delayDetail !== null) {
                  return (
                    <DelayWaitCard
                      key={key}
                      detail={delayDetail}
                      stillWaiting={row.status === 'RUNNING'}
                      stepFailed={row.status === 'FAILED'}
                    />
                  );
                }
                return <AttemptCard key={key} attempt={attempt} />;
              })}
            </Fragment>
          ))
        )}
      </div>
    </Drawer>
  );
}
